import React, { useState, useEffect } from 'react';
import apiConfig from '../config/environment';
import './DailyRumor.css';

interface Rumor {
  category: string;
  text: string;
}

interface DailyRumorResponse {
  date: { year: number; month: number; day: number };
  rumor: Rumor;
}

interface DailyRumorProps {
  year: number;
  month: number;
  day: number;
  collapsed?: boolean;
}

const DailyRumor: React.FC<DailyRumorProps> = ({ year, month, day, collapsed = false }) => {
  const [rumor, setRumor] = useState<Rumor | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'CHAR': return '👤';
      case 'ORG': return '🏴‍☠️';
      case 'LOC': return '🏰';
      case 'ITEM': return '✨';
      case 'EVENT': return '📅';
      default: return '❓';
    }
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'CHAR': return '#e74c3c';
      case 'ORG': return '#8e44ad';
      case 'LOC': return '#2980b9';
      case 'ITEM': return '#f39c12';
      case 'EVENT': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  useEffect(() => {
    const fetchRumor = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${apiConfig.API_BASE_URL}/api/rumors/daily/${year}/${month}/${day}`);
        
        if (response.ok) {
          const data: DailyRumorResponse = await response.json();
          setRumor(data.rumor);
        } else {
          setError('Kein Gerücht für diesen Tag verfügbar');
        }
      } catch (err) {
        setError('Fehler beim Laden des Gerüchts');
        console.error('Error fetching daily rumor:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRumor();
  }, [year, month, day]);

  if (loading) {
    return (
      <div className="daily-rumor loading">
        <div className="rumor-header">
          <span className="rumor-title">Tagesgerücht</span>
        </div>
        <div className="rumor-content">Lade Gerücht...</div>
      </div>
    );
  }

  if (error || !rumor) {
    return null;
  }

  return (
    <div className="daily-rumor">
      <div 
        className="rumor-header" 
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{ cursor: 'pointer' }}
      >
        <span className="rumor-title">Tagesgerücht</span>
        <span className="collapse-indicator">
          {isCollapsed ? '▼' : '▲'}
        </span>
      </div>
      
      {!isCollapsed && (
        <div className="rumor-content">
          <div 
            className="rumor-category" 
            style={{ 
              backgroundColor: getCategoryColor(rumor.category),
              color: 'white'
            }}
          >
            <span className="category-icon">{getCategoryIcon(rumor.category)}</span>
            <span className="category-name">{rumor.category}</span>
          </div>
          <p className="rumor-text">{rumor.text}</p>
        </div>
      )}
    </div>
  );
};

export default DailyRumor;